// ---------------------------------------------------------------------------
// sageTransactions.ts — fold a deal detail's Sage postings into the groups the
// Transactions tab renders, on both clients.
//
// IT LIVES IN shared/ BECAUSE BOTH CLIENTS RENDER THIS TAB. A copy in frontend/
// and another in mobile/ would be two things to keep in lockstep for no reason
// — the platform carries several such pairs only because api/ cannot depend on
// this package, and that constraint does not apply here.
//
// THE CLASSIFICATION IS NOT IN THIS FILE, AND THAT IS THE POINT. Which class an
// account falls in (1xxxx assets · 2xxxx debt · 4xxxx income · 5xxxx/6xxxx
// expenses · everything else "Other") is decided once, on the API, by
// sageAccountClassInfo() in api/src/sageAccounts.js — the module that already
// owns every other rule about a Sage account number. Each row arrives carrying
// its class key, its heading and its sort order, so this file only buckets and
// totals. A headings-and-ordering table restated here would be a second copy of
// the ranges, and it would drift the first time a class moved.
//
// ROW ORDER WITHIN A GROUP IS THE ORDER IT ARRIVED IN (the API sorts newest
// first), so grouping never silently reorders a ledger. Only the GROUPS are
// sorted, by the order the server assigned them.
//
// A row with no class — an older API that does not send one, or a payload that
// lost the field — lands in "Other" rather than being dropped. Silently hiding
// a posting from a financial list is the one failure worth designing against;
// showing it under a heading that admits it is unclassed is not.
// ---------------------------------------------------------------------------

export interface SageTransactionRow {
  transactionDate?: string | null;
  description?: string | null;
  amount?: number | string | null;
  accountNumber?: string | null;
  accountClass?: string | null;
  accountClassLabel?: string | null;
  accountClassOrder?: number | null;
}

export interface SageTransactionGroup<T = SageTransactionRow> {
  key: string;
  label: string;
  order: number;
  rows: T[];
  total: number;
}

const FALLBACK_LABEL = "Other";
// Sorts after every real class, so an unclassed row never leads the list.
const FALLBACK_ORDER = 99;

export function groupSageTransactions<T extends SageTransactionRow>(
  rows: T[] | null | undefined,
): SageTransactionGroup<T>[] {
  const byKey = new Map<string, SageTransactionGroup<T>>();

  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row) continue;
    const key = row.accountClass || "other";
    if (!byKey.has(key)) {
      const order = Number(row.accountClassOrder);
      byKey.set(key, {
        key,
        label: row.accountClassLabel || FALLBACK_LABEL,
        order: Number.isFinite(order) ? order : FALLBACK_ORDER,
        rows: [],
        total: 0,
      });
    }
    const group = byKey.get(key)!;
    group.rows.push(row);
    // mssql hands DECIMAL back as a string, so coerce rather than concatenate.
    group.total += Number(row.amount) || 0;
  }

  return [...byKey.values()].sort(
    (a, b) => a.order - b.order || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0),
  );
}
